import { useState, useEffect, useRef, useCallback } from 'react';
import { auth, firestore } from '../firebase';
import { doc, getDoc, setDoc, increment, serverTimestamp, addDoc, collection } from 'firebase/firestore';

const API_WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:5000';

const calcElo = (myElo, opponentElo, won) => {
  const K        = 32;
  const expected = 1 / (1 + Math.pow(10, (opponentElo - myElo) / 400));
  return Math.round(myElo + K * ((won ? 1 : 0) - expected));
};

export const PHASE = {
  IDLE:    'idle',
  QUEUING: 'queuing',
  BATTLE:  'battle',
  RESULT:  'result',
};

export const DURATION_SECONDS_MAP = {
  '2min':  120,
  '5min':  300,
  '20min': 1200,
};
export const DEFAULT_DURATION_ID = '2min';

export const useBattle = () => {
  const wsRef    = useRef(null);
  const timerRef = useRef(null);

  // Keep refs to challenge data so saveResultToFirestore can always access
  // the latest values even when called from inside a closure
  const challengeRef    = useRef(null);
  const constraintsRef  = useRef([]);
  const minScoreRef     = useRef(75);

  const [phase,             setPhase]             = useState(PHASE.IDLE);
  const [timeLeft,          setTimeLeft]          = useState(120);
  const [challenge,         setChallenge]         = useState(null);
  const [constraints,       setConstraints]       = useState([]);
  const [constraintKey,     setConstraintKey]     = useState(null);
  const [minimumScore,      setMinimumScore]      = useState(75);
  const [opponent,          setOpponent]          = useState(null);
  const [userPrompt,        setUserPrompt]        = useState('');
  const [isSubmitting,      setIsSubmitting]      = useState(false);
  const [myScore,           setMyScore]           = useState(null);
  const [myScoreDetails,    setMyScoreDetails]    = useState(null);
  const [opponentSubmitted, setOpponentSubmitted] = useState(false);
  const [result,            setResult]            = useState(null);
  const [error,             setError]             = useState(null);
  const [eloData,           setEloData]           = useState(null);
  const [oppEloData,        setOppEloData]        = useState(null);

  const [myEntry, setMyEntry] = useState(null);

  const [challengeCode,  setChallengeCode]  = useState(null);
  const [challengeError, setChallengeError] = useState(null);

  const loadMyEntry = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const snap = await getDoc(doc(firestore, 'battleLeaderboard', user.uid));
      if (snap.exists()) setMyEntry(snap.data());
    } catch (e) {
      console.error('ELO load failed:', e);
    }
  }, []);

  useEffect(() => { loadMyEntry(); }, [loadMyEntry]);

  const connectWs = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState <= 1) return;

    const ws     = new WebSocket(API_WS_URL);
    wsRef.current = ws;

    ws.onmessage = (e) => {
      try { handleMessage(JSON.parse(e.data)); } catch { /* ignore bad frames */ }
    };
    ws.onerror = () => setError('Connection error. Please try again.');
    ws.onclose = () => {
      setPhase(p => {
        if (p === PHASE.BATTLE || p === PHASE.QUEUING) {
          setError('Connection lost. Please try again.');
          return PHASE.IDLE;
        }
        return p;
      });
    };
  }, []);

  const handleMessage = useCallback((msg) => {
    switch (msg.type) {

      case 'queued':
      case 'no_opponent_found':
        setPhase(PHASE.QUEUING);
        break;

      case 'challenge_created':
        setChallengeCode(msg.code);
        setChallengeError(null);
        break;

      case 'challenge_expired':
        setChallengeCode(null);
        setChallengeError('Your challenge link expired (10 min limit). Generate a new one.');
        setPhase(PHASE.IDLE);
        break;

      case 'challenge_invalid':
        setChallengeError(msg.message);
        setChallengeCode(null);
        setPhase(PHASE.IDLE);
        break;

      case 'battle_start':
        clearTimeout(timerRef.current);
        setChallengeCode(null);
        setChallengeError(null);

        // Store in refs so saveResultToFirestore can read them reliably
        challengeRef.current   = msg.challenge;
        constraintsRef.current = msg.constraints   || [];
        minScoreRef.current    = msg.minimumScore  ?? 75;

        setChallenge(msg.challenge);
        setConstraints(msg.constraints);
        setConstraintKey(msg.constraintKey);
        setMinimumScore(msg.minimumScore ?? 75);
        setOpponent(msg.opponent);
        setUserPrompt('');
        setMyScore(null);
        setMyScoreDetails(null);
        setOpponentSubmitted(false);
        setResult(null);
        setError(null);
        setEloData(null);
        setOppEloData(null);
        setPhase(PHASE.BATTLE);
        startTimer(msg.durationSeconds ?? 120);
        break;

      case 'your_score':
        setMyScore(msg.score);
        setMyScoreDetails(msg.details ?? null);
        setIsSubmitting(false);
        break;

      case 'opponent_submitted':
        setOpponentSubmitted(true);
        break;

        case 'battle_result':
  clearTimeout(timerRef.current);
  setResult(msg);
  setPhase(PHASE.RESULT);
  console.log('SAVING:', challengeRef.current, constraintsRef.current, minScoreRef.current);
  saveResultToFirestore(msg, challengeRef.current, constraintsRef.current, minScoreRef.current);
  break;
      case 'battle_error':
        setError(msg.message);
        setPhase(PHASE.IDLE);
        break;

      case 'battle_cancelled':
        setError(msg.reason ?? 'Battle cancelled.');
        setPhase(PHASE.IDLE);
        break;

      case 'queue_cancelled':
        setPhase(PHASE.IDLE);
        break;

      default:
        break;
    }
  }, []);

  const startTimer = (seconds) => {
    setTimeLeft(seconds);
    clearTimeout(timerRef.current);
    const tick = () => {
      setTimeLeft(t => {
        if (t <= 1) return 0;
        timerRef.current = setTimeout(tick, 1000);
        return t - 1;
      });
    };
    timerRef.current = setTimeout(tick, 1000);
  };

  const saveResultToFirestore = async (msg, challengeData, constraintsData, minScore) => {
    const user = auth.currentUser;
    if (!user || msg.reason === 'opponent_disconnected') return;

    try {
      const myResult  = msg.players.find(p => p.userId === user.uid);
      const oppResult = msg.players.find(p => p.userId !== user.uid);
      if (!myResult) return;

      const won      = msg.winnerId === user.uid;
      const leaderRef = doc(firestore, 'battleLeaderboard', user.uid);
      const snap      = await getDoc(leaderRef);
      const current   = snap.exists() ? snap.data() : { elo: 1000, wins: 0, losses: 0, battles: 0 };

      let oppElo      = 1000;
      let oppCurrent  = null;
      const isBot     = oppResult?.userId?.startsWith('bot_');

      if (!isBot && oppResult?.userId) {
        const oppSnap = await getDoc(doc(firestore, 'battleLeaderboard', oppResult.userId));
        if (oppSnap.exists()) {
          oppCurrent = oppSnap.data();
          oppElo     = oppCurrent.elo ?? 1000;
        }
      }

      const newElo  = calcElo(current.elo, oppElo, won);
      const delta   = newElo - current.elo;

      const oppNewElo  = calcElo(oppElo, current.elo, !won);
      const oppDelta   = oppNewElo - oppElo;
      const newOppEloData = {
        oldElo: oppElo,
        newElo: oppNewElo,
        delta:  oppDelta,
      };

      await setDoc(leaderRef, {
        userId:       user.uid,
        username:     user.displayName || user.email?.split('@')[0] || 'Anonymous',
        elo:          newElo,
        wins:         increment(won ? 1 : 0),
        losses:       increment(won ? 0 : 1),
        battles:      increment(1),
        lastBattleAt: serverTimestamp(),
      }, { merge: true });

      const newEloData = { oldElo: current.elo, newElo, delta };
      setEloData(newEloData);
      setOppEloData(newOppEloData);

      await loadMyEntry();

      await addDoc(collection(firestore, 'battleHistory'), {
        playerIds:            [user.uid, oppResult?.userId || null],
        players:              msg.players,
        winnerId:             msg.winnerId,
        reason:               msg.reason || null,
        // ── Challenge data from refs (reliable) ──
        challengeTitle:       challengeData?.title       || '',
        challengeDescription: challengeData?.description || '',
        challengeObjective:   challengeData?.objective   || '',
        challengeRemarks:     challengeData?.remarks     || '',
        constraints:          constraintsData            || [],
        difficulty:           challengeData?.difficulty  || 'medium',
        minimumScore:         minScore                   || 75,
        eloChanges:           {
          [user.uid]:                   newEloData,
          [oppResult?.userId || 'opp']: newOppEloData,
        },
        playedAt: serverTimestamp(),
      });

    } catch (e) {
      console.error('Failed to save battle result:', e);
    }
  };

  const joinQueue = useCallback((options = {}) => {
    const user = auth.currentUser;
    if (!user) { setError('Please log in to battle.'); return; }

    const durationId = options.duration || DEFAULT_DURATION_ID;

    setError(null);
    connectWs();

    const attempt = () => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) { setTimeout(attempt, 200); return; }
      ws.send(JSON.stringify({
        type:     'join_queue',
        userId:   user.uid,
        username: user.displayName || user.email?.split('@')[0] || 'Anonymous',
        duration: durationId,
      }));
    };
    attempt();
  }, [connectWs]);

  const cancelQueue = useCallback(() => {
    wsRef.current?.send(JSON.stringify({ type: 'cancel_queue' }));
    setPhase(PHASE.IDLE);
  }, []);

  const createChallenge = useCallback((durationId = DEFAULT_DURATION_ID) => {
    const user = auth.currentUser;
    if (!user) { setError('Please log in to battle.'); return; }

    setError(null);
    setChallengeError(null);
    setChallengeCode(null);
    connectWs();

    const attempt = () => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) { setTimeout(attempt, 200); return; }
      ws.send(JSON.stringify({
        type:     'create_challenge',
        userId:   user.uid,
        username: user.displayName || user.email?.split('@')[0] || 'Anonymous',
        duration: durationId,
      }));
    };
    attempt();
    setPhase(PHASE.QUEUING);
  }, [connectWs]);

  const joinChallenge = useCallback((code) => {
    const user = auth.currentUser;
    if (!user) { setError('Please log in to battle.'); return; }
    if (!code) { setChallengeError('No challenge code provided.'); return; }

    setChallengeError(null);
    setError(null);
    connectWs();

    const attempt = () => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) { setTimeout(attempt, 200); return; }
      ws.send(JSON.stringify({
        type:     'join_challenge',
        userId:   user.uid,
        username: user.displayName || user.email?.split('@')[0] || 'Anonymous',
        code,
      }));
    };
    attempt();
  }, [connectWs]);

  const cancelChallenge = useCallback(() => {
    setChallengeCode(null);
    setChallengeError(null);
    wsRef.current?.send(JSON.stringify({ type: 'cancel_queue' }));
    setPhase(PHASE.IDLE);
  }, []);

  const submitBattle = useCallback(async () => {
    if (!userPrompt.trim() || isSubmitting || myScore !== null) return;
    const user = auth.currentUser;
    if (!user) return;

    setIsSubmitting(true);
    wsRef.current?.send(JSON.stringify({
      type:   'submit_battle',
      userId: user.uid,
      prompt: userPrompt,
    }));
  }, [userPrompt, isSubmitting, myScore]);

  const resetBattle = useCallback(() => {
    clearTimeout(timerRef.current);
    setPhase(PHASE.IDLE);
    setChallenge(null);
    setConstraints([]);
    setConstraintKey(null);
    setOpponent(null);
    setUserPrompt('');
    setMyScore(null);
    setMyScoreDetails(null);
    setOpponentSubmitted(false);
    setResult(null);
    setError(null);
    setTimeLeft(120);
    setEloData(null);
    setOppEloData(null);
    setChallengeCode(null);
    setChallengeError(null);
    challengeRef.current   = null;
    constraintsRef.current = [];
    minScoreRef.current    = 75;
  }, []);

  useEffect(() => () => {
    clearTimeout(timerRef.current);
    wsRef.current?.close();
  }, []);

  return {
    phase,
    timeLeft,
    challenge,
    constraints,
    minimumScore,
    opponent,
    userPrompt, setUserPrompt,
    isSubmitting,
    myScore,
    myScoreDetails,
    opponentSubmitted,
    result,
    error,
    eloData,
    oppEloData,
    myEntry,
    joinQueue,
    cancelQueue,
    submitBattle,
    resetBattle,
    challengeCode,
    challengeError,
    createChallenge,
    joinChallenge,
    cancelChallenge,
  };
};